import React, { useMemo } from 'react';

import AssigneeFilter from './assignee';
import TaskTypeFilter from './type';
import { Label } from '@buildoutinc/blueprint-react/ui/Label';
import OfficeFilter from './office';
import ParentTypeFilter from './parent_type';
import QueryFilter from './query';
import isEqual from 'lodash/isEqual';

import { useTasks } from '../context/tasks_context';
import { isFilterEnabled } from './helpers';

import type { FilterProps } from './types';
import { Col, Row } from 'react-bootstrap-5';
import { Button } from 'components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEraser } from '@fortawesome/pro-solid-svg-icons';

export interface Props {
  assignee?: FilterProps;
  children?: React.ReactNode;
  type?: FilterProps;
  features?: {
    marketingListings?: boolean;
    taskAttachmentExceptions?: boolean;
  };
  options: {
    offices: {
      label: string;
      value: number;
    }[];
    users: {
      id: number;
      name: string;
    }[];
  };
  parent?: FilterProps;
  withLabels?: boolean;
}

export default function TasksIndexFilters({ children, features, options, withLabels, ...props }: Props) {
  const { filters, filterDefaults, changeFilters } = useTasks();

  const hasChanges = !isEqual(filters, filterDefaults);
  const users = useMemo(() => options.users.filter(option => option.id), []);
  const filterProps = { ...filters, features, onChange: changeFilters };
  const filterEntries = [
    {
      key: 'query',
      label: 'Search',
      visible: true,
      style: { minWidth: '300px' } as React.CSSProperties,
      render: <QueryFilter {...filterProps} onChange={changes => changeFilters(changes, true)} />,
    },
    {
      key: 'parent',
      label: 'Source',
      visible: isFilterEnabled(props, 'parent'),
      render: <ParentTypeFilter {...filterProps} />,
    },
    {
      key: 'type',
      label: 'Type',
      visible: isFilterEnabled(props, 'type'),
      render: <TaskTypeFilter {...filterProps} />,
    },
    {
      key: 'office',
      label: 'Office',
      visible: isFilterEnabled(props, 'office') && options.offices.length > 0,
      render: <OfficeFilter {...filterProps} offices={options.offices} />,
    },
    {
      key: 'assignee',
      label: 'Assigned To',
      visible: isFilterEnabled(props, 'assignee'),
      render: <AssigneeFilter {...filterProps} users={users} />,
    },
  ];

  return (
    <div className="search-page">
      <Row>
        <Col>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            {filterEntries.filter(entry => entry.visible).map(filter => (
              <div className="d-flex flex-column gap-2" key={filter.key} style={filter.style}>
                {withLabels && <Label>{filter.label}</Label>}
                {filter.render}
              </div>
            ))}
            {hasChanges && (
              <Button
                style={{ marginTop: 'auto' }}
                variant="ghost"
                onClick={() => changeFilters(filterDefaults)}
              >
                <FontAwesomeIcon icon={faEraser} />
                Clear Filters
              </Button>
            )}
          </div>
        </Col>
        {children &&
          <Col xs="auto">
            {children}
          </Col>
        }
      </Row>
    </div>
  );
}
